import React, {useContext, useEffect, useState, useMemo, useCallback} from "react";
import {Accordion, AccordionDetails, AccordionSummary, Box, Button, Chip,
	Grid, IconButton, InputAdornment, Menu, MenuItem, Paper, TextField, Typography, useTheme} from "@mui/material";
import {ExpandMore, MoreVertRounded, VisibilityRounded} from "@mui/icons-material";
import {useTranslation} from "react-i18next";
import {isMobile} from "react-device-detect";
import {useRecoilState, useRecoilValue} from "recoil";
import {useMutation} from "@tanstack/react-query";
import dayjs from "dayjs";
import { checkEquipmentsState, checkOutInModeState, checkOutInStepState, checkVehicleStatusState, equipmentsEventState,
	eventsTypeState, existingDamageTypeState, raFetchedDataState, summaryState, extraCostInputState,
	checkOutInEventsListState} from "../state";
import Preview from "../../preview/Preview";
import CheckStepper from "../check-stepper/CheckStepper";
import {ELECTRIC_ENGINE_POWER_CODE, ELECTRIC_MEASUREMENT_UNITS, LIQUID_MEASUREMENT_UNITS, LOCALES, TERMIC_HYBRID_ENGINE_POWER_CODE} from "../../config";
import {IDamage, IEvent, TLocales} from "../../models/models";
import {fetchCheckOutInConfirmForm, fetchCheckOutInGetSnapshotBlogById, fetchDamagePrice, fetchCheckInExtra} from "../services/http.services";
import {snackbarTypes} from "../../../../util/state/globalVariables";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import CustomLoader from "../../../../util/component/CustomLoader";
import {formatCurrency, getLocaleByLang} from "../../utils";
import {REG_EXPRESSION_INPUT_FLOAT_NUMBER} from "../config";
import {getBillingCustomerId, isEffectiveDateChanged} from "../services/utils";
import { checkInOutErrorsManagement } from "./utils";
import useOperationTranslation from "../../../operation/hooks/useOperationTranslation";

const Summary = () => {
	const theme = useTheme();
	const {t, i18n} = useTranslation(['network', 'translation']);
	const { t: t2 } = useTranslation('operation');
	const appLanguage = useTranslation();
	const { dispatchGlobalData } = useContext(GlobalContext);

	const [openPreviewAttach, setOpenPreviewAttach] = useState<boolean>(false);
	const [filePreview, setFilePreview] = useState<any>(null);
	const [damagePrice, setDamagePrice] = useState<any>(null);
	// extra cost field values
	const [extraCostInputValues, setExtraCostInputValues] = useRecoilState<any>(extraCostInputState);
	// modalità attiva
	const checkOutInMode = useRecoilValue(checkOutInModeState);
	const [checkStep, setCheckStep] = useRecoilState(checkOutInStepState);
	// fetch from BE
	const raFetchedData = useRecoilValue(raFetchedDataState);
	// inserito	=> kms, tank, battery & photos
	const checkVehicle = useRecoilValue(checkVehicleStatusState);
	// equipment
	const equipmentsEvent = useRecoilValue(equipmentsEventState);
	const checkEquipments = useRecoilValue(checkEquipmentsState);

	const existingIntDamage = useRecoilValue(existingDamageTypeState('internal'));
	const existingExtDamage = useRecoilValue(existingDamageTypeState('external'));
	const eventsInternal = useRecoilValue<any>(eventsTypeState('internal'));
	const eventsExternal = useRecoilValue<any>(eventsTypeState('external'));

	const [anchorEl, setAnchorEl] = useState(null);
	const [selectedDamage, setSelectedDamage] = useState(null);
	// CheckIn extra
	const [raCheckInExtra, setRaCheckInExtra] = useState<any>(null);

	const [newMissingEquips, setNewMissingEquips] = useState([]);
	const [missingDotationAmount, setMissingDotationAmount] = useState(0);
	const [damageExcess, setDamageExcess] = useState<number | null>(null);
	const [totEvents, setTotEvents] = useState<any>(null);
	const [eventsDmgAmount, setEventsDmgAmount] = useState<any>(null);
	const [coveredCost, setCoveredCost] = useState<any>(null);
	const [totDmgsEquips, setTotDmgsEquips] = useState<any>(null);
	const [disableFuelExtra, setDisableFuelExtra] = useState<boolean>(true);
	const [disableBatteryExtra, setDisableBatteryExtra] = useState<boolean>(true);
	//view refuelServiceAmount field
	const [viewRefuelServiceAmountExtra, setViewRefuelServiceAmountExtra] = useState<boolean>(false);
	//disable refuelServiceAmount field
	const [disableRefuelServiceAmountExtra, setDisableRefuelServiceAmountExtra] = useState<boolean>(true);
	// LPP-21950: gli extra sono raccolti nello step precedente e mostrati qui in sola lettura.
	const washExtra = extraCostInputValues?.washExtra ?? '0';
	const washDescription = extraCostInputValues?.washDescription ?? '';
	const washPhoto = extraCostInputValues?.washPhoto ?? null;
	const recoveryExtra = extraCostInputValues?.recoveryExtra ?? '0';
	const recoveryDescription = extraCostInputValues?.recoveryDescription ?? '';
	const recoveryPhoto = extraCostInputValues?.recoveryPhoto ?? null;
	const [summary, setSummary] = useRecoilState(summaryState);

	const [viewOneWayServiceAmountExtra, setViewOneWayServiceAmountExtra] = useState<boolean>(false);
	const [disableOneWayServiceAmountExtra] = useState<boolean>(true);
	const [viewOneWayRefoundableServiceAmountExtra, setViewOneWayRefoundableServiceAmountExtra] = useState<boolean>(false);
	const [disableOneWayRefoundServiceAmountExtra] = useState<boolean>(true);
	const [OneWayCost, setOneWayCost] = useState<any>(null);

	const eventOptions = useRecoilValue(checkOutInEventsListState);
	const operationTranslation = useOperationTranslation();
	
	const locale = useMemo((): 'it-IT' | 'en-IN' | 'fr-FR' => {
		return getLocaleByLang(appLanguage.i18n.language);
	}, [appLanguage.i18n.language]);

	const billingCustomerId = useMemo(() => {
		if (['in', 'subIn', 'out'].includes(checkOutInMode)) {
			return getBillingCustomerId(raFetchedData);
		} else {
			return null;
		}
	}, [raFetchedData]);

	useEffect( () => {
		if (['in', 'subIn'].includes(checkOutInMode) && !!eventsExternal &&
				!!checkEquipments && !!raCheckInExtra && !!damagePrice &&
				raFetchedData?.quotationInfo?.damageExcess !== undefined &&
				raFetchedData.quotationInfo.damageExcess !== null
		) {
			// Tot equip mancanti
			// const missingDotationAmount =
				// newMissingEquips.length > 0 ?
				// 	newMissingEquips
				// 		.map((equip: any) => equip.data.priceInCents)
				// 		.reduce((partialSum: number, next: number) => partialSum + next, 0) :
				// 	0;

			// Franchiggia — LPP-21950: usa la franchigia EFFETTIVA abbattuta dalla copertura attiva
			// (SC/SCDW/STP), che il backend calcola in ExtraCheckInResponse.damageExcess
			// (CheckInServiceImpl.setDamageExcess), non quella grezza di quotationInfo. Con SUPER_COVER
			// attiva effectiveDamageExcess=0 → i danni sotto franchigia non vengono piu' mostrati come
			// dovuti, coerentemente con la fattura che non li addebita (invoiceExtra usa lo stesso
			// abbattimento). Fallback alla franchigia grezza se il campo non arriva (nessuna copertura
			// o flussi che non lo valorizzano).
			const damageExcess = raCheckInExtra?.damageExcess != null
				? raCheckInExtra.damageExcess
				: parseInt(raFetchedData.quotationInfo.damageExcess);

			// events summary calc
			const totEvents = {};
			for (const event of eventsExternal) {
				// @ts-ignore
				totEvents[event.id] =
					event.damages
						.map((damage: IDamage) =>
							damagePrice?.damagePrices
								.find((e: { damageId: number }) =>
									e.damageId === damage.id)?.price)
						.reduce((sum: number, currentValue: any) =>
							sum + parseInt(currentValue), 0)
			}

			// Tot of the events by damageExcess
			const eventsDmgAmount = {};
			for (const [id, value] of Object.entries(totEvents)) {
				// @ts-ignore
				eventsDmgAmount[id] = value > damageExcess ? damageExcess : value;
			}

			// Costi coperti (Danni e Parti Mancanti)
			let coveredCost = 0;
			// @ts-ignore
			for (const [id, value] of Object.entries(totEvents)) {
				// @ts-ignore
				coveredCost = coveredCost + (value > damageExcess ? value - damageExcess : 0);
			}

			let totDmgsEquips = missingDotationAmount;
			for (const [id, value] of Object.entries(eventsDmgAmount)) {
				// @ts-ignore
				totDmgsEquips = totDmgsEquips + value;
			}

			// Total
			//const fuelExtra = raCheckInExtra?.amountResponseExtraRefill?.extraTermicAmount ?? 0;
			//const batteryExtra = raCheckInExtra?.amountResponseExtraRefill?.extraSocAmount ?? 0;
			//const refuelServiceAmount = raCheckInExtra.amountResponseExtraRefill?.refuelServiceAmount ?? 0;
			const fuelExtra = parseFloat((extraCostInputValues?.fuelExtra ?? '0').replace(',', '.'));
			const batteryExtra = parseFloat((extraCostInputValues?.batteryExtra ?? '0').replace(',', '.'));
			const refuelServiceAmount = extraCostInputValues?.refuelServiceAmount ?? '0';
			const extraKmAmount = raCheckInExtra?.amountResponseExtraKm?.amountTaxIncluded ?? 0;
			const extraDaysAmount = raCheckInExtra?.amountResponseExtraDays?.amountTaxIncluded ?? 0;
			const extraAncillaryAmount = raCheckInExtra?.amountResponseExtraDays?.extraAncillaryAmount ?? 0;
			// LPP-21950 (D34): `postSaleAncillaryPeriodPreviewAmount` (delta di periodo degli accessori aggiunti
			// al banco dopo la firma) concorre anche al totale mostrato del riepilogo, cioe' `summary.total` reso
			// sotto la label raCheckOutIn.summary.amount.label.total (la label totalAmountToPay rende invece
			// totDmgsEquips, solo danni ed equipaggiamenti, e resta intatta). Il campo sta
			// sulla radice della response, NON dentro amountResponseExtraDays, quindi non e' compreso in
			// extraAncillaryAmount qui sopra: si somma una volta sola, come addendo a se stante del totale.
			// Nessun doppio conteggio con la voce accessori a video: quella (vedi resa della label
			// extraDetail.label.ancillary) fa la propria somma leggendo direttamente `raCheckInExtra` e non passa
			// da questa const ne' dal totale. `raCheckInExtra` non viene mutato, perche' Signature lo rimanda al
			// backend come extraAncillaryAmount dispositivo; `total` invece finisce solo a video e nella guard
			// isNaN del bottone. Il campo puo' mancare dal JSON e vale 0.
			const postSaleAncillaryPreviewAmount = raCheckInExtra?.postSaleAncillaryPeriodPreviewAmount ?? 0;
			// LPP-23910 (difetto Q2): `postSaleAncillaryDeltaPreviewAmount` (radice della response) e' il netto
			// degli accessori aggiunti/rimossi dopo la firma che verra' addebitato al settlement del check-in.
			// Come il periodo D34 e' un addendo a se' stante del totale (nessun doppio conteggio con
			// extraAncillaryAmount ne' col periodo), reso come voce dedicata. Puo' mancare dal JSON e vale 0.
			const postSaleAncillaryDeltaPreviewAmount = raCheckInExtra?.postSaleAncillaryDeltaPreviewAmount ?? 0;
			const extraAmountTaxIncluded = extraCostInputValues?.extraAmountTaxIncluded ?? '0';
			const refundableAmountTaxIncluded = extraCostInputValues?.refundableAmountTaxIncluded ?? '0';
			const oneWayServiceAmount = extraCostInputValues?.oneWayServiceAmount ?? '0';
			const deltaAmountTaxIncluded = extraCostInputValues?.deltaAmountTaxIncluded ?? '0';
			
			//mostro refoundable solo se diverso da zero
			if(refundableAmountTaxIncluded === '0')
				setViewOneWayRefoundableServiceAmountExtra(false);
			else{
				setViewOneWayRefoundableServiceAmountExtra(true);
			}
			//mostro amount solo se diverso da zero
			setViewOneWayServiceAmountExtra(false);
			//sommo l'extra one way se diverso da zero altrimenti sommo il delta del ricalcolato
			//altrimenti sommo zero e sto apposto con il totale
			let onewayamounttoadd = 0;
			if(extraAmountTaxIncluded !== '0'){
				onewayamounttoadd = parseFloat(extraAmountTaxIncluded.replace(',', '.'));
				setOneWayCost(extraAmountTaxIncluded);
				setViewOneWayServiceAmountExtra(true);
			}else if(oneWayServiceAmount !== '0' && deltaAmountTaxIncluded !== '0'){
				onewayamounttoadd = parseFloat(deltaAmountTaxIncluded.replace(',', '.'));
				setOneWayCost(deltaAmountTaxIncluded);
				setViewOneWayServiceAmountExtra(true);
			}

			// LPP-22945: extra costi lavaggio/recupero (manuali, sempre al valore pieno, bypass copertura).
			const washExtraAmount = parseFloat((washExtra ?? '0').replace(',', '.'));
			const recoveryExtraAmount = parseFloat((recoveryExtra ?? '0').replace(',', '.'));

			const total = parseFloat((totDmgsEquips/100).toString())
								+ fuelExtra + batteryExtra
								+ parseFloat(refuelServiceAmount.replace(',', '.'))
								+ parseFloat((extraKmAmount/100).toString()) 
								+ parseFloat((extraDaysAmount/100).toString()) 
								+ parseFloat((extraAncillaryAmount/100).toString())
								+ parseFloat((postSaleAncillaryPreviewAmount/100).toString())
								+ parseFloat((postSaleAncillaryDeltaPreviewAmount/100).toString())
								+ washExtraAmount + recoveryExtraAmount
								+ onewayamounttoadd;
			

			setDamageExcess(damageExcess);
			setTotEvents(totEvents);
			setEventsDmgAmount(eventsDmgAmount);
			setCoveredCost(coveredCost);
			setTotDmgsEquips(totDmgsEquips);
			setSummary({
				missingDotationAmount: missingDotationAmount,
				damagePrice: damagePrice,
				raCheckInExtra: raCheckInExtra,
				fuelExtra: 			fuelExtra,
				extraTermicLitres: 	raCheckInExtra.amountResponseExtraRefill.extraTermicLitres,
				termicFuelPrice: 	raCheckInExtra.amountResponseExtraRefill.termicFuelPrice,
				batteryExtra: 		batteryExtra,
				extraSocKws: 		raCheckInExtra.amountResponseExtraRefill.extraSocKws,
				extraSocKwsPrice: 	raCheckInExtra.amountResponseExtraRefill.extraSocKwsPrice,
				refuelServiceAmount: refuelServiceAmount,
				total: 				total*100,
				extraKms: 			raCheckInExtra.amountResponseExtraKm?.extraKms,
				extraAmountTaxIncluded: extraAmountTaxIncluded,
				refundableAmountTaxIncluded: refundableAmountTaxIncluded,
				oneWayServiceAmount: oneWayServiceAmount,
				deltaAmountTaxIncluded: deltaAmountTaxIncluded,
				// LPP-22945: extra costi lavaggio/recupero (stringhe €, convertite in centesimi al finalize).
				washExtra: 			washExtra,
				washDescription: 	washDescription,
				washPhoto: 			washPhoto,
				recoveryExtra: 		recoveryExtra,
				recoveryDescription: recoveryDescription,
				recoveryPhoto: 		recoveryPhoto,
			});
		}
		else if (checkOutInMode === 'out' && !!eventsExternal && !!checkEquipments && !!damagePrice) {
			for (const event of eventsExternal) {
				event.damages
					.map((damage: IDamage) =>
						damagePrice?.damagePrices
							.find((e: { damageId: number }) =>
								e.damageId === damage.id)?.price)
					.reduce((sum: number, currentValue: any) =>
						sum + parseInt(currentValue), 0)
			}

			setSummary({
				damagePrice: damagePrice
			});
		}
	},[
		eventsExternal, checkEquipments, damagePrice, raCheckInExtra,
		raFetchedData.quotationInfo,
		extraCostInputValues,
		washExtra, washDescription, washPhoto, recoveryExtra, recoveryDescription, recoveryPhoto
	])

	const handleOpenMenu = (event:any , damage: any) => {
		setAnchorEl(event.currentTarget);
		setSelectedDamage(damage);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const handleOnChangeExtraCost = (e: any, field: 'batteryExtra' | 'fuelExtra' | 'refuelServiceAmount' | 'oneWayExtraServiceAmount' | 'oneWayRefoundServiceAmount' | 'oneWayServiceAmount') => {
		let regExp = REG_EXPRESSION_INPUT_FLOAT_NUMBER;
		let value = e.target.value;
		let ind_0 = value.indexOf('0');
		let ind_v = value.indexOf(',');
		if (value.length > 1 && ind_0 === 0 && ind_v === -1) {
			value = value.substring(1);
		} else if (ind_v === 0) {
			value = '0' + value;
		}
		if (e.target.value.match(regExp)) {
			setExtraCostInputValues({
				...extraCostInputValues,
				batteryExtra: 	field === 'batteryExtra' ? value : extraCostInputValues?.batteryExtra,
				fuelExtra:		field === 'fuelExtra' ? value : extraCostInputValues?.fuelExtra,
				refuelServiceAmount: field === 'refuelServiceAmount' ? value : extraCostInputValues?.refuelServiceAmount,
				extraTermicLitres: 	extraCostInputValues.extraTermicLitres,
				termicFuelPrice: 	extraCostInputValues.termicFuelPrice,
				extraSocKws: 		extraCostInputValues.extraSocKws,
				extraSocKwsPrice: 	extraCostInputValues.extraSocKwsPrice,
				missingAllRefuelInfo: 		extraCostInputValues?.missingAllRefuelInfo,
				missingRefuelAncillaryPrice: extraCostInputValues?.missingRefuelAncillaryPrice,
				missingDetectionPrice: 		extraCostInputValues?.missingDetectionPrice,
				extraAmountTaxIncluded:     field === 'oneWayExtraServiceAmount' ? value : extraCostInputValues?.extraAmountTaxIncluded,
				refundableAmountTaxIncluded: field === 'oneWayRefoundServiceAmount' ? value : extraCostInputValues?.refundableAmountTaxIncluded,
				oneWayServiceAmount : field === 'oneWayServiceAmount' ? value : extraCostInputValues?.oneWayServiceAmount,
				deltaAmountTaxIncluded : extraCostInputValues?.deltaAmountTaxIncluded,
			});
		} else if (e.target.value === '') {
			setExtraCostInputValues({
				...extraCostInputValues,
				batteryExtra: 	field === 'batteryExtra' ? '0' : extraCostInputValues?.batteryExtra,
				fuelExtra:		field === 'fuelExtra' ? '0' : extraCostInputValues?.fuelExtra,
				refuelServiceAmount: field === 'refuelServiceAmount' ? '0' : extraCostInputValues?.refuelServiceAmount,
				extraTermicLitres: 	extraCostInputValues.extraTermicLitres,
				termicFuelPrice: 	extraCostInputValues.termicFuelPrice,
				extraSocKws: 		extraCostInputValues.extraSocKws,
				extraSocKwsPrice: 	extraCostInputValues.extraSocKwsPrice,
				missingAllRefuelInfo: 		extraCostInputValues?.missingAllRefuelInfo,
				missingRefuelAncillaryPrice: extraCostInputValues?.missingRefuelAncillaryPrice,
				missingDetectionPrice: 		extraCostInputValues?.missingDetectionPrice,
				extraAmountTaxIncluded:     field === 'oneWayExtraServiceAmount' ? '0' : extraCostInputValues?.extraAmountTaxIncluded,
				refundableAmountTaxIncluded:     field === 'oneWayRefoundServiceAmount' ? '0' : extraCostInputValues?.refundableAmountTaxIncluded,
				oneWayServiceAmount : field === 'oneWayServiceAmount' ? value : extraCostInputValues?.oneWayServiceAmount,
				deltaAmountTaxIncluded : extraCostInputValues?.deltaAmountTaxIncluded,
			});
		} else {
			setExtraCostInputValues(extraCostInputValues);
		}
	}

	const viewDefaultError = (mess: string, type = snackbarTypes.error) => {
		dispatchGlobalData({
			type: 'SET_SNACKBAR',
			payload: {
				snackbar: {
					message: t(mess),
					type: type,
					show: true,
					autoClose: true,
				}
			}
		});
	}

	/** Confirm Form Mutation */
	const confirmFormMutation = useMutation(
		fetchCheckOutInConfirmForm,
		{
			onSuccess: async (response: any) => {
				if (!response?.[0]) {
					viewDefaultError('translation:error.tryLater');
				}
			},
			onError: (error) => {
				viewDefaultError('translation:error.tryLater');
			}
		});

	/** Fetch Damage With Price */
	const damagePriceMutation = useMutation(
		fetchDamagePrice,
		{
			onSuccess: (response: any) => {
				setDamagePrice(response);
			},
			onError: (error) => {
				viewDefaultError('translation:error.tryLater');
			}
		});

	/** Fetch Km & Days Extra */
	const fetchCheckInExtraMutation = useMutation(
		fetchCheckInExtra,
		{
			onSuccess: (response: any) => {
				setRaCheckInExtra(response);
				// set fields values
				setExtraCostInputValues((current: any) => ({
					...current,
					batteryExtra: 			!!response.amountResponseExtraRefill?.extraSocAmount ? (response.amountResponseExtraRefill.extraSocAmount / 100).toFixed(2).toString() : '0',
					fuelExtra:				!!response.amountResponseExtraRefill?.extraTermicAmount ? (response.amountResponseExtraRefill.extraTermicAmount / 100).toFixed(2).toString() : '0',
					refuelServiceAmount: 	!!response.amountResponseExtraRefill?.refuelServiceAmount ? (response.amountResponseExtraRefill.refuelServiceAmount/100).toFixed(2).toString() : '0',
					extraTermicLitres:		`${(response.amountResponseExtraRefill?.extraTermicLitres ?? 0)} ${LIQUID_MEASUREMENT_UNITS}`,
					termicFuelPrice: 		`${((response.amountResponseExtraRefill?.termicFuelPrice ?? 0) / 1000).toFixed(3)} €`,
					extraSocKws: 			`${(response.amountResponseExtraRefill?.extraSocKws ?? 0 )} ${ELECTRIC_MEASUREMENT_UNITS}`,
					extraSocKwsPrice: 		`${((response.amountResponseExtraRefill?.extraSocKwsPrice ?? 0 ) / 1000).toFixed(3)} €`,
					missingAllRefuelInfo: 		!response.amountResponseExtraRefill?.missingAllRefuelInfo,  // true -> false -> didsable False (puoi editare)
					missingRefuelAncillaryPrice: !response.amountResponseExtraRefill?.missingRefuelAncillaryPrice,
					missingDetectionPrice: 		!response.amountResponseExtraRefill?.missingDetectionPrice,
					extraAmountTaxIncluded:     !!response.extraOneWayResponse?.extraAmountTaxIncluded ? (response.extraOneWayResponse.extraAmountTaxIncluded/100).toFixed(2).toString() : '0',
					refundableAmountTaxIncluded:     !!response.extraOneWayResponse?.refundableAmountTaxIncluded ? (response.extraOneWayResponse.refundableAmountTaxIncluded/100).toFixed(2).toString() : '0',
					oneWayServiceAmount : !!response.extraOneWayResponse?.recalculatedAmountTaxIncluded ? (response.extraOneWayResponse.recalculatedAmountTaxIncluded/100).toFixed(2).toString() : '0',
					deltaAmountTaxIncluded :  !!response.extraOneWayResponse?.deltaAmountTaxIncluded ? (response.extraOneWayResponse.deltaAmountTaxIncluded/100).toFixed(2).toString() : '0',
				}));

				initExtraCostFields(response);

				// manage  missingAllRefuelInfo/missingRefuelAncillaryPrice/missingDetectionPrice errors
				if (response.amountResponseExtraRefill.missingAllRefuelInfo) {
					viewDefaultError('common.error.missingAllRefuelInfo', snackbarTypes.warning);
				}
				else if (response.amountResponseExtraRefill.missingRefuelAncillaryPrice) {
					viewDefaultError('common.error.refuelPricing', snackbarTypes.warning);
				}
				else if (response.amountResponseExtraRefill.missingDetectionPrice) {
					viewDefaultError('common.error.priceNotFound', snackbarTypes.warning);
				}

				/**
				 extraCostInputValues.amountResponseExtraRefill.missingAllRefuelInfo
				 extraCostInputValues.amountResponseExtraRefill.missingRefuelAncillaryPrice
				 extraCostInputValues.amountResponseExtraRefill.missingDetectionPrice

				 * dentro amountResponseExtraRefill sono state aggiunte 3 booleanE
				 * (missingAllRefuelInfo, missingRefuelAncillaryPrice, missingDetectionPrice)
				 *
				 * // in questi casi status 200
				 * missingRefuelAncillaryPrice === true -> rendere editabile il campo refuel service amount e
				 * mostrare il messaggio -> "common.error.refuelPricing"
				 *  	campo required, vabene anche 0, non sono ammessi  valori negativi
				 *
				 *
				 * missingDetectionPrice === true -> rendere editabile/eidtabili i campi sul costo del carburante e
				 * mostrare il messaggio "common.error.priceNotFound"
				 *		campi required, non va bene 0, non sono ammessi valori negativi
				 *
				 * missingAllRefuelInfo === true -> rendere editabile/editabili i campi refuelServiceAmount, fuelExtra, batteryExtra
				 * 		campi required, fE e bE > 0 (effetto controllo se e solo se il campo è editabile)
				 * 		common.error.missingAllRefuelInfo
				 *
				 * tutti mess sono di tipo qwrning (giallo)
				 */

				let messageoneWayExtraServiceAmountMessage = "";
				let messagoneWayRefoundServiceAmount = "";
				let messagoneWayServiceAmount = "";
				//  "oneWayServiceAmount" : "Le montant du service One Way a été recalculé"
				if(extraCostInputValues?.extraAmountTaxIncluded !== '0' && extraCostInputValues?.extraAmountTaxIncluded !== undefined){
					messageoneWayExtraServiceAmountMessage = t("raCheckOutIn.summary.extraDetail.label.oneWayExtraServiceAmountMessage");
				}
				if(extraCostInputValues?.refundableAmountTaxIncluded !== '0' && extraCostInputValues?.refundableAmountTaxIncluded !== undefined){
					messagoneWayRefoundServiceAmount = t("raCheckOutIn.summary.extraDetail.label.oneWayRefoundServiceAmountMessage");
				}
				if(extraCostInputValues?.oneWayServiceAmount !== '0' && extraCostInputValues?.oneWayServiceAmount !== undefined){
					messagoneWayServiceAmount = t("raCheckOutIn.summary.extraDetail.label.oneWayServiceAmountMessage");
				}
				const messageOneway = messageoneWayExtraServiceAmountMessage + '\n' +
                      messagoneWayRefoundServiceAmount + '\n' +
                      messagoneWayServiceAmount;
					  
				if(messageoneWayExtraServiceAmountMessage !== ""
					|| messagoneWayRefoundServiceAmount !== ""
					|| messagoneWayServiceAmount !== ""){
						viewDefaultError(messageOneway, snackbarTypes.warning);
				}

			},
			onError: (error: any) => {

				setRaCheckInExtra({
					amountResponseExtraDays: null,
					amountResponseExtraKm: null,
					amountResponseExtraRefill: {
						extraTermicAmount: 0,
						extraSocAmount: 0,
						refuelServiceAmount: null,
						refuelServiceTaxCode: null,
						extraTermicLitres: null,
						termicFuelPrice: null,
						extraSocKws: null,
						extraSocKwsPrice: null,
						oneWayServiceAmount: null,
						refundableAmountTaxIncluded: null,
						extraAmountTaxIncluded: null
					},
					missingAllRefuelInfo: true,
					missingRefuelAncillaryPrice: true,
					missingDetectionPrice: true,
					flagError: true   // disabilita pulsante per qualsiasi errore
				});
				const errorPack = checkInOutErrorsManagement(error);
				viewDefaultError(errorPack.mess, errorPack.snBarType);
				setDisableBatteryExtra(false);
				setDisableFuelExtra(false);
			}
		});

	const handleContinue = async (e: React.SyntheticEvent) => {
		e.stopPropagation();

		// LPP-22945 (feedback INT): foto + descrizione obbligatorie se l'extra costo lavaggio/recupero e' valorizzato.
		if (['in', 'subIn'].includes(checkOutInMode)) {
			const washAmt = parseFloat((washExtra ?? '0').replace(',', '.'));
			const recoveryAmt = parseFloat((recoveryExtra ?? '0').replace(',', '.'));
			if (washAmt > 0 && (!washPhoto || !washDescription?.trim())) {
				viewDefaultError('raCheckOutIn.summary.extraDetail.error.washAttachmentRequired', snackbarTypes.error);
				return;
			}
			if (recoveryAmt > 0 && (!recoveryPhoto || !recoveryDescription?.trim())) {
				viewDefaultError('raCheckOutIn.summary.extraDetail.error.recoveryAttachmentRequired', snackbarTypes.error);
				return;
			}
		}

		if (['oprIn', 'oprOut', 'ddt','dataUpd'].includes(checkOutInMode)) {
			const intExtEvents = eventsExternal.concat(eventsInternal);
			const confirmFormList: any[] = [];
			intExtEvents
				.map((event: IEvent) => {
					const eventDmgIds = event.damages.map((dmg: IDamage) => dmg.id);
					confirmFormList.push({
						formId: event.id,
						inspectionKM: checkVehicle.kmsOnCheck,
						inspectionFuelLeve: checkVehicle.tankLevelOnCheck?.code,
						inspectionBatteryLevel: checkVehicle.batteryLevelOnCheck?.code,
						expectedCheckInDate: raFetchedData.bookingDetail?.expectedCheckInDate,
						effectiveCheckInDate: 	!!checkVehicle?.effectiveDateTime ? checkVehicle.effectiveDateTime.toISOString() : null,
						missingDotationAmount:  !!missingDotationAmount ? missingDotationAmount : 0,
						gridAmount: !!damagePrice?.damagePrices ?
										damagePrice.damagePrices
											.filter((dmg: any) => eventDmgIds.includes(dmg.id))
											.map((dmg: any) => dmg.price)
											.reduce((partialSum: number, next: number) => partialSum + next, 0) :
										0,
						damageOwnerSapid: billingCustomerId,
						keybox:	checkVehicle.keyBoxServiceApplicable && isEffectiveDateChanged(checkVehicle.effectiveDateTime, checkVehicle.now)
					});
					return event;
				});

			// add equipment event
			if (!!equipmentsEvent.id) {
				confirmFormList.push({
					formId: 				equipmentsEvent.id,
					inspectionKM: 			checkVehicle.kmsOnCheck,
					inspectionFuelLevel: 	checkVehicle.tankLevelOnCheck?.code,
					inspectionBatteryLevel: checkVehicle.batteryLevelOnCheck?.code,
					expectedCheckInDate:	raFetchedData.bookingDetail?.pickupDate, //'da ra expectedCheckInDate - expected dropoff Data', o checkIn: expectedCheckInDate
					effectiveCheckInDate: 	!!checkVehicle?.effectiveDateTime ? checkVehicle.effectiveDateTime.toISOString() : null,
					gridAmount: 0,
					missingDotationAmount: 	missingDotationAmount,
					damageOwnerSapid: 		billingCustomerId
				});
			}

			/* Confirm all Forms */
			if (confirmFormList.length > 0) {
				await confirmFormMutation.mutateAsync({tenant: raFetchedData.idTenant, forms: {formDataList: confirmFormList}} as any);
			}

			if (!!raFetchedData.oprData?.oprCallback) {
				raFetchedData.oprData.oprCallback({
					vehicleCheck: 		checkVehicle,
					existingDamages: {
						internal: 		existingIntDamage,
						external: 		existingExtDamage
					},
					eventsInternal: 	eventsInternal,
					eventsExternal: 	eventsExternal,
					equipmentsEvent: 	equipmentsEvent
				});
			}
		} else {
			/* check if fuelExtra > 0 e batteryExtra > 0 */
			if (checkOutInMode.includes('in')) {
				const fE = Number(extraCostInputValues.fuelExtra);
				const bE = Number(extraCostInputValues.batteryExtra);
				if (!extraCostInputValues.missingAllRefuelInfo &&
					(TERMIC_HYBRID_ENGINE_POWER_CODE.includes(raFetchedData.vehicle.enginePowerGroupCode) && fE <= 0 || ELECTRIC_ENGINE_POWER_CODE.includes(raFetchedData.vehicle.enginePowerGroupCode) && bE <= 0))
				{
					viewDefaultError("common.error.missingAllRefuelInfoValueLTZ", snackbarTypes.warning);
					return;
				}

			}
			setCheckStep({
				active: checkStep.active + 1,
				numSteps: checkStep.numSteps
			});
		}
	}

	const fetchCheckOutInGetSnapshotBlogByIdMutation = useMutation(
		fetchCheckOutInGetSnapshotBlogById,
		{
			onSuccess: (response: any) => {
				setFilePreview({file: "data:image/png;base64," + response.img});
				setOpenPreviewAttach(true);
			},
			onError: (error) => {
				dispatchGlobalData({
					type: 'SET_SNACKBAR',
					payload: {
						snackbar: {
							message: t('translation:error.tryLater'),
							type: snackbarTypes.error,
							show: true,
							autoClose: true,
						}
					}
				});
			}
		});

	const handleViewSnapshot = (e: React.SyntheticEvent, snapshot: string | number) => {
		e.stopPropagation();

		if (typeof snapshot === "number") {
			fetchCheckOutInGetSnapshotBlogByIdMutation.mutate({snapshotId: snapshot} as any);
		} else {
			setFilePreview({file: snapshot});
			setOpenPreviewAttach(true);
		}
	}

	const handleClosePreview = () => {
		setFilePreview(null);
		setOpenPreviewAttach(false);
	}

	const initExtraCostFields = (data: any) => {
		setDisableBatteryExtra(!!data.amountResponseExtraRefill?.extraSocAmount);
		setDisableFuelExtra(!!data.amountResponseExtraRefill?.extraTermicAmount);
		if ((!!data.amountResponseExtraRefill?.extraTermicAmount && data.amountResponseExtraRefill.extraTermicAmount > 0) ||
			(!!data.amountResponseExtraRefill?.extraSocAmount && data.amountResponseExtraRefill.extraSocAmount > 0) ||
			!extraCostInputValues?.missingRefuelAncillaryPrice)
		{
			setViewRefuelServiceAmountExtra(true);
			if (!!data.amountResponseExtraRefill.refuelServiceAmount && data.amountResponseExtraRefill.refuelServiceAmount > 0) {
				setDisableRefuelServiceAmountExtra(true);
			} else {
				setDisableRefuelServiceAmountExtra(false);
			}
		}
	}

	useEffect(() => {
		// get damage/price for confirm form damage
		if (eventsExternal.length > 0) {
			damagePriceMutation.mutate({
				tenant:  raFetchedData.idTenant,
				carClass: raFetchedData.vehicle.carClassDescription,
				damageFormIds: eventsExternal.map((event: IEvent) => event.id)
			} as any);
		}
		else {
			setDamagePrice({damagePrices: []});
		}

		// LPP-25993: l'elenco delle parti mancanti serve anche al check-out; l'importo resta solo al check-in
		const newMissingEquips = (checkEquipments?.equipments ?? []).filter((equip: any) => equip.data.associated && equip.missing);
		setNewMissingEquips(newMissingEquips);

		if (['in', 'subIn'].includes(checkOutInMode)) {
			// Tot equip mancanti
			const missingDotationAmount =
				newMissingEquips.length > 0 ?
					newMissingEquips
						.map((equip: any) => equip.data.priceInCents)
						.reduce((partialSum: number, next: number) => partialSum + next, 0) :
					0;
			setMissingDotationAmount(missingDotationAmount);

			const kmsOnStart = !!raFetchedData?.checkOut?.kmsOnCheck ? raFetchedData.checkOut.kmsOnCheck : raFetchedData.vehicle.kilometersOnStart;
			// confirm all forms
			const intExtEvents = eventsExternal.concat(eventsInternal);

			const effetiveDropOffLocation = checkVehicle?.dropOffLocation?.code ?? null;

			let reqExtra: any = {
				idRentalAgreement: raFetchedData.idRentalAgreement,
				reservedRateId: raFetchedData.quotationInfo?.reservedRateId,
				bknOfferId: raFetchedData.bookingDetail?.offerId,
				effectiveKm: parseFloat(checkVehicle.kmsOnCheck) - parseFloat(kmsOnStart),
				effectiveCheckInDate: !!checkVehicle?.effectiveDateTime ? checkVehicle.effectiveDateTime.toISOString() : null,
				termicLevel: checkVehicle.tankLevelOnCheck?.code ?? null,
				socLevel: checkVehicle.batteryLevelOnCheck?.code ?? null,

				damageFormIds: 				intExtEvents.map((event: IEvent) => event.id),
				missingEquipmentFormId: 	equipmentsEvent?.id,
				missingEquipmentAmount: 	missingDotationAmount,
				effetiveDropOffLocation: effetiveDropOffLocation,
				extraWashAmount: Math.round((parseFloat((washExtra ?? '0').replace(',', '.')) || 0) * 100),
				extraWashDescription: washDescription || null,
				extraRecoveryAmount: Math.round((parseFloat((recoveryExtra ?? '0').replace(',', '.')) || 0) * 100),
				extraRecoveryDescription: recoveryDescription || null
			};
			if (!fetchCheckInExtraMutation.isLoading) {
				fetchCheckInExtraMutation.mutate(reqExtra as any);
			} else {
				console.log("Mutation already in progress");
			}
		}
	},[]);

	const getEventLabel = useCallback((opt:any) => {
		const code = eventOptions.find((eo:any) => eo.id === opt.id)?.code

		return operationTranslation(`masterDataDamage.event.${code?.toLowerCase().replace('.', '_')}` as any)
	}, [eventOptions, i18n.language])

	return (<Paper elevation={1} sx={{p: theme.spacing(2), mb: theme.spacing(2)}}>

		<Box sx={{mb: theme.spacing(2)}}>
			<Typography variant={"h6"} sx={{display: "flex", justifyContent: "center"}}>
				{t("raCheckOutIn.summary.header.title")}
			</Typography>
		</Box>

		<Grid container columnSpacing={2} sx={{pl: theme.spacing(2)}}>
			<Grid item xs={12}
				  sx={{bgcolor: theme.palette.primary.main,
					  borderTopLeftRadius: 8,
					  borderTopRightRadius: 8,
					  py: theme.spacing(1)
				  }}>
				<Typography variant={"h6"} sx={{color: theme.palette.neutral.light}}>
					{t("raCheckOutIn.summary.raStatus.title")}
				</Typography>
			</Grid>

			{/* RA Detail */}
			{
				['in', 'subIn', 'out'].includes(checkOutInMode) &&
				<>
					<Grid container item xs={12} md={6} sx={{bgcolor: theme.palette.neutral.main}}>
						<Grid item xs={6} sx={{py: theme.spacing(1)}}>
							<Typography variant={"body"} component={"div"}>
								{t("raCheckOutIn.summary.raStatus.label.raNumber")}
							</Typography>
							<Typography variant={"body"} component={"div"}>
								<strong>
									{raFetchedData.raNumber}
								</strong>
							</Typography>
						</Grid>

						{/* Check-In */}
						{
							['in', 'subIn'].includes(checkOutInMode) &&
							<Grid item xs={6} sx={{bgcolor: theme.palette.neutral.main, py: theme.spacing(1)}}>
								<Typography variant={"body"} component={"div"}>
									{t("raCheckOutIn.summary.raStatus.label.renewalsSubstitution")}
								</Typography>
								<Typography variant={"body"} component={"div"}>
									<strong>
										{raFetchedData.renewalsSubstitution}
									</strong>
								</Typography>
							</Grid>
						}

						<Grid item xs={6} sx={{bgcolor: theme.palette.neutral.main}}>
							<Typography variant={"body"} component={"div"}>
								{t("raCheckOutIn.summary.raStatus.label.raOwner")}
							</Typography>
							<Typography variant={"body"} component={"div"}>
								<strong>
									{raFetchedData.raOwner.firstName} {raFetchedData.raOwner.lastName}
								</strong>
							</Typography>
						</Grid>

						<Grid item xs={12} sx={{bgcolor: theme.palette.neutral.main}}></Grid>
						<Grid item xs={12} sx={{bgcolor: theme.palette.neutral.main}}></Grid>
						<Grid item xs={12} sx={{bgcolor: theme.palette.neutral.main}}></Grid>
					</Grid>
				</>
			}

			<Grid container item xs={12} md={6} sx={{bgcolor: theme.palette.neutral.main}}>
				<Grid item xs={6} sx={{py: theme.spacing(1)}}>
					<Typography variant={"body"} component={"div"}>
						{t("raCheckOutIn.summary.raStatus.label.vehicleModel")}
					</Typography>
					<Typography variant={"body"} component={"div"}>
						<strong>
							{(!!raFetchedData.vehicle?.brandDescription ? raFetchedData.vehicle.brandDescription + ' ' : '') + (!!raFetchedData.vehicle?.modelDescription ? raFetchedData.vehicle.modelDescription : '')}
						</strong>
					</Typography>
				</Grid>

				<Grid item xs={6} sx={{py: theme.spacing(1)}}>
					<Typography variant={"body"} component={"div"}>
						{t("raCheckOutIn.summary.raStatus.label.vehiclePlate")}
					</Typography>
					<Typography variant={"body"} component={"div"}>
						<strong>
							{raFetchedData.vehicle.plate.description}
						</strong>
					</Typography>
				</Grid>

				{/* Check-Out */}
				{
					checkOutInMode === 'out' &&
					<>
						<Grid item xs={6} sx={{py: theme.spacing(1)}}>
							<Typography variant={"body"} component={"div"}>
								{t("raCheckOutIn.summary.raStatus.label.pickUpDate")}
							</Typography>
							<Typography variant={"body"} component={"div"}>
								<strong>
									{dayjs(raFetchedData.checkOut.effectiveCheckoutDate).format('DD/MM/YYYY HH:mm')}
								</strong>
							</Typography>
						</Grid>

						<Grid item xs={6} sx={{py: theme.spacing(1)}}>
							<Typography variant={"body"} component={"div"}>
								{t("raCheckOutIn.summary.raStatus.label.effectivePickUpDate")}
							</Typography>
							<Typography variant={"body"} component={"div"}>
								<strong>
									{dayjs().format('DD/MM/YYYY HH:mm')}
								</strong>
							</Typography>
						</Grid>
					</>
				}

				{/* Check-In */}
				{
					['in', 'subIn'].includes(checkOutInMode) &&
					<>
						<Grid item xs={6} sx={{py: theme.spacing(1)}}>
							<Typography variant={"body"} component={"div"}>
								{t("raCheckOutIn.summary.raStatus.label.pickUpLocation")}
							</Typography>
							<Typography variant={"body"} component={"div"}>
								<strong>
									{raFetchedData.checkOut?.dmsIdentificationCode?.description}
								</strong>
							</Typography>
						</Grid>

						<Grid item xs={6} sx={{py: theme.spacing(1)}}>
							<Typography variant={"body"} component={"div"}>
								{t("raCheckOutIn.summary.raStatus.label.dropOffLocation")}
							</Typography>
							<Typography variant={"body"} component={"div"}>
								<strong>
									{checkVehicle?.dropOffLocation?.description}
								</strong>
							</Typography>
						</Grid>

						<Grid item xs={6} sx={{py: theme.spacing(1)}}>
							<Typography variant={"body"} component={"div"}>
								{t("raCheckOutIn.summary.raStatus.label.dropOffDate")}
							</Typography>
							<Typography variant={"body"} component={"div"}>
								<strong>
									{dayjs(raFetchedData.checkIn.expectedCheckInDate).format('DD/MM/YYYY HH:mm')}
								</strong>
							</Typography>
						</Grid>

						<Grid item xs={6} sx={{py: theme.spacing(1)}}>
							<Typography variant={"body"} component={"div"}>
								{t("raCheckOutIn.summary.raStatus.label.effectiveDropOffDate")}
							</Typography>
							<Typography variant={"body"} component={"div"}>
								<strong>
									{!!checkVehicle?.effectiveDateTime ? checkVehicle.effectiveDateTime?.format('DD/MM/YYYY HH:mm') : ''}
								</strong>
							</Typography>
						</Grid>
					</>
				}
			</Grid>

			{/* Opr mode */}
			{
				!['out', 'in', 'subIn'].includes(checkOutInMode) &&
				<Grid container item xs={12} md={6} sx={{bgcolor: theme.palette.neutral.main}} />
			}

			{/* Vehicle Status */}
			<Grid item xs={12} sx={{mt: theme.spacing(2)}}>
				<Typography variant={"h6"}>
					{t("raCheckOutIn.summary.kmFillStatus.title")}
				</Typography>
			</Grid>

			<Grid item xs={12} sx={{p: theme.spacing(1)}}>
				<Chip label={
					<Typography variant={"body"} component={"div"}>
						{t("raCheckOutIn.summary.kmFillStatus.label.km")}
						<Typography variant={"h6"} component={"span"}>
							{checkVehicle.kmsOnCheck}
						</Typography>
					</Typography>
				}
					  sx={{mr: theme.spacing(1), my: theme.spacing(1)}}
				/>

				{
					TERMIC_HYBRID_ENGINE_POWER_CODE.includes(raFetchedData.vehicle.enginePowerGroupCode) &&
					<Chip label={
						<Typography variant={"body"} component={"div"}>
							{t("raCheckOutIn.summary.kmFillStatus.label.fuelLevel")}
							<Typography variant={"h6"} component={"span"}>
								{checkVehicle.tankLevelOnCheck?.description}
							</Typography>
						</Typography>
					}
						  sx={{mr: theme.spacing(1)}}
					/>
				}

				{
					TERMIC_HYBRID_ENGINE_POWER_CODE.includes(raFetchedData.vehicle.enginePowerGroupCode) &&
					!!raCheckInExtra?.amountResponseExtraRefill?.extraTermicLitres &&
					<Chip label={
						<Typography variant={"body"} component={"div"}>
							{t('raCheckOutIn.summary.kmFillStatus.label.extraTermicLitres')}
							<Typography variant={"body"} component={"span"}>
								<strong>
									{extraCostInputValues?.extraTermicLitres}
								</strong>
							</Typography>
						</Typography>
					}
						  sx={{mr: theme.spacing(1)}}
					/>
				}

				{
					TERMIC_HYBRID_ENGINE_POWER_CODE.includes(raFetchedData.vehicle.enginePowerGroupCode) &&
					!!raCheckInExtra?.amountResponseExtraRefill?.extraTermicLitres &&
					<Chip label={
						<Typography variant={"body"} component={"div"}>
							{t('raCheckOutIn.summary.kmFillStatus.label.termicFuelPrice')}
							<Typography variant={"body"} component={"span"}>
								<strong>
									{extraCostInputValues?.termicFuelPrice}
								</strong>
							</Typography>
						</Typography>
					}
						  sx={{mr: theme.spacing(1)}}
					/>
				}

				{
					ELECTRIC_ENGINE_POWER_CODE.includes(raFetchedData.vehicle.enginePowerGroupCode) &&
					<Chip label={
						<Typography variant={"body"} component={"div"}>
							{t("raCheckOutIn.summary.kmFillStatus.label.batteryLevel")}
							<Typography variant={"body"} component={"span"}>
								<strong>
									{checkVehicle.batteryLevelOnCheck?.description}
								</strong>
							</Typography>
						</Typography>
					}
						  sx={{mr: theme.spacing(1)}}
					/>
				}

				{
					ELECTRIC_ENGINE_POWER_CODE.includes(raFetchedData.vehicle.enginePowerGroupCode) &&
					!!raCheckInExtra?.amountResponseExtraRefill?.extraSocKws &&
					<Chip label={
						<Typography variant={"body"} component={"div"}>
							{t('raCheckOutIn.summary.kmFillStatus.label.extraSocKws')}
							<Typography variant={"body"} component={"span"}>
								<strong>
									{extraCostInputValues?.extraSocKws}
								</strong>
							</Typography>
						</Typography>
					}
						  sx={{mr: theme.spacing(1)}}
					/>
				}

				{
					ELECTRIC_ENGINE_POWER_CODE.includes(raFetchedData.vehicle.enginePowerGroupCode) &&
					!!raCheckInExtra?.amountResponseExtraRefill?.extraSocKws &&
					<Chip label={
						<Typography variant={"body"} component={"div"}>
							{t('raCheckOutIn.summary.kmFillStatus.label.extraSocKwsPrice')}
							<Typography variant={"body"} component={"span"}>
								<strong>
									{extraCostInputValues?.extraSocKwsPrice}
								</strong>
							</Typography>
						</Typography>
					}
						  sx={{mr: theme.spacing(1)}}
					/>
				}
			</Grid>

			{/* Check-In  -  Extra Detail */}
			{
				['in', 'subIn'].includes(checkOutInMode) &&
				<>
					<Grid item xs={7} md={3} sx={{p: theme.spacing(1)}}>
						<Typography variant={"h6"}>
							{t("raCheckOutIn.summary.extraDetail.label.days")}
						</Typography>
					</Grid>
					<Grid item xs={3} md={2} sx={{p: theme.spacing(1)}}>
						{
							!!raCheckInExtra?.amountResponseExtraDays?.extraDays &&
							<Typography variant={"h6"}>
								{raCheckInExtra.amountResponseExtraDays.extraDays} {t('raCheckOutIn.summary.extraDetail.label.numDays')}
							</Typography>
						}
					</Grid>
					<Grid item xs={2} md={1} sx={{display: "flex", alignItems: "center", justifyContent: "flex-end", pr: theme.spacing(1)}}>
						<Typography variant={"h6"}>
							{!!raCheckInExtra?.amountResponseExtraDays?.amountTaxIncluded ?
								formatCurrency(locale as LOCALES, raCheckInExtra?.amountResponseExtraDays?.amountTaxIncluded) :
								formatCurrency(locale as LOCALES, 0)}
						</Typography>
					</Grid>

					<Grid item xs={7} md={3} sx={{p: theme.spacing(1)}}>
						<Typography variant={"h6"}>
							{t("raCheckOutIn.summary.extraDetail.label.km")}
						</Typography>
					</Grid>
					<Grid item xs={3} md={2}>
						{
							!!raCheckInExtra?.amountResponseExtraKm?.extraKm &&
							<Typography variant={"h6"}>
								{raCheckInExtra.amountResponseExtraKm.extraKm} {t('raCheckOutIn.summary.extraDetail.label.numKms')}
							</Typography>
						}
					</Grid>
					<Grid item xs={2} md={1} sx={{display: "flex", alignItems: "center", justifyContent: "flex-end", pr: theme.spacing(1)}}>
						<Typography variant={"h6"}>
							{!!raCheckInExtra?.amountResponseExtraKm?.amountTaxIncluded ?
								formatCurrency(locale as LOCALES, raCheckInExtra?.amountResponseExtraKm?.amountTaxIncluded) :
								formatCurrency(locale as LOCALES, 0)}
						</Typography>
					</Grid>

					<Grid item xs={10} md={5} sx={{p: theme.spacing(1)}}>
						<Typography variant={"h6"}>
							{t("raCheckOutIn.summary.extraDetail.label.ancillary")}
						</Typography>
					</Grid>
					<Grid item xs={2} md={1} sx={{display: "flex", alignItems: "center", justifyContent: "flex-end", pr: theme.spacing(1)}}>
						{/* LPP-21950 (T2.64septies) / LPP-23910 (difetto Q2): la voce "Accessori" somma per la sola
						    visualizzazione gli accessori per giorni extra (`extraAncillaryAmount`), il delta di periodo
						    post-firma (`postSaleAncillaryPeriodPreviewAmount`, D34) e il netto delta base post-firma che
						    verra' addebitato al settlement (`postSaleAncillaryDeltaPreviewAmount`, Q2). Coerente col
						    totale, che somma gli stessi tre addendi. Mostra e non addebita: `raCheckInExtra` non viene
						    mutato (Signature lo rimanda al backend come extraAncillaryAmount). I campi radice possono
						    mancare dal JSON e valgono 0. */}
						<Typography variant={"h6"}>
							{formatCurrency(locale as LOCALES,
								(raCheckInExtra?.amountResponseExtraDays?.extraAncillaryAmount ?? 0)
								+ (raCheckInExtra?.postSaleAncillaryPeriodPreviewAmount ?? 0)
								+ (raCheckInExtra?.postSaleAncillaryDeltaPreviewAmount ?? 0))}
						</Typography>
					</Grid>

					{
						TERMIC_HYBRID_ENGINE_POWER_CODE.includes(raFetchedData.vehicle.enginePowerGroupCode) &&
						<>
							<Grid item xs={9} md={4} sx={{p: theme.spacing(1)}}>
								<Typography variant={"h6"}>
									{t("raCheckOutIn.summary.extraDetail.label.fuel")}
								</Typography>
							</Grid>

							<Grid item xs={3} md={2} sx={{display: "flex", alignItems: "center", justifyContent: "flex-end", pr: theme.spacing(1)}}>
								<TextField
									value={extraCostInputValues?.fuelExtra ?? '0'}
									onChange={(e: React.SyntheticEvent) => handleOnChangeExtraCost(e, 'fuelExtra')}
									name='fuelExtra'
									fullWidth
									InputProps={{
										inputProps: {
											style: { textAlign: "right" }
										},
										endAdornment: <InputAdornment position="end">{'€'}</InputAdornment>
									}}
									disabled={disableFuelExtra || checkVehicle.nonRunningVehicle ||
										!!extraCostInputValues?.missingDetectionPrice}
								/>
							</Grid>
						</>
					}

					{
						ELECTRIC_ENGINE_POWER_CODE.includes(raFetchedData.vehicle.enginePowerGroupCode) &&
						<>
							<Grid item xs={9} md={4} sx={{p: theme.spacing(1)}}>
								<Typography variant={"h6"}>
									{t("raCheckOutIn.summary.extraDetail.label.battery")}
								</Typography>
							</Grid>

							<Grid item xs={3} md={2} sx={{display: "flex", alignItems: "center", justifyContent: "flex-end", pr: theme.spacing(1)}}>
								<TextField
									value={extraCostInputValues?.batteryExtra ?? '0'}
									onChange={(e: React.SyntheticEvent)=>handleOnChangeExtraCost(e, 'batteryExtra')}
									name='batteryExtra'
									fullWidth
									InputProps={{
										inputProps: {
											style: { textAlign: "right" }
										},
										endAdornment: <InputAdornment position="end">{'€'}</InputAdornment>
									}}
									disabled={disableBatteryExtra || checkVehicle.nonRunningVehicle ||
										!!extraCostInputValues?.missingDetectionPrice}
								/>
							</Grid>
						</>
					}

					{
						viewRefuelServiceAmountExtra &&
						<>
							<Grid item xs={9} md={4} sx={{p: theme.spacing(1)}}>
								<Typography variant={"h6"}>
									{t("raCheckOutIn.summary.extraDetail.label.refuelServiceAmount")}
								</Typography>
							</Grid>

							<Grid item xs={3} md={2} sx={{display: "flex", alignItems: "center", justifyContent: "flex-end", pr: theme.spacing(1)}}>
								<TextField
									value={extraCostInputValues?.refuelServiceAmount ?? '0'}
									onChange={(e: React.SyntheticEvent) => handleOnChangeExtraCost(e, 'refuelServiceAmount')}
									name='refuelServiceAmount'
									fullWidth
									InputProps={{
										inputProps: {
											style: { textAlign: "right" }
										},
										endAdornment: <InputAdornment position="end">{'€'}</InputAdornment>
									}}
									disabled={disableRefuelServiceAmountExtra ||
										!!extraCostInputValues?.missingRefuelAncillaryPrice}
								/>
							</Grid>
						</>
					}

					{
						/* LPP-22945: importo lavaggio valorizzato nello step precedente e mostrato in sola lettura. */
						['in', 'subIn'].includes(checkOutInMode) &&
						<>
							<Grid item xs={9} md={4} sx={{p: theme.spacing(1)}}>
								<Typography variant={"h6"}>
									{t("raCheckOutIn.summary.extraDetail.label.washExtra")}
								</Typography>
							</Grid>
							<Grid item xs={3} md={2} sx={{display: "flex", alignItems: "center", justifyContent: "flex-end", pr: theme.spacing(1)}}>
								<TextField
									value={washExtra ?? '0'}
									name='washExtra'
									fullWidth
									InputProps={{
										inputProps: {style: {textAlign: "right"}},
										endAdornment: <InputAdornment position="end">{'€'}</InputAdornment>
									}}
									disabled
								/>
							</Grid>
						</>
					}

					{
						/* LPP-22945: importo recupero valorizzato nello step precedente e mostrato in sola lettura. */
						['in', 'subIn'].includes(checkOutInMode) &&
						<>
							<Grid item xs={9} md={4} sx={{p: theme.spacing(1)}}>
								<Typography variant={"h6"}>
									{t("raCheckOutIn.summary.extraDetail.label.recoveryExtra")}
								</Typography>
							</Grid>
							<Grid item xs={3} md={2} sx={{display: "flex", alignItems: "center", justifyContent: "flex-end", pr: theme.spacing(1)}}>
								<TextField
									value={recoveryExtra ?? '0'}
									name='recoveryExtra'
									fullWidth
									InputProps={{
										inputProps: {style: {textAlign: "right"}},
										endAdornment: <InputAdornment position="end">{'€'}</InputAdornment>
									}}
									disabled
								/>
							</Grid>
						</>
					}

					{
						['in', 'subIn'].includes(checkOutInMode) && viewOneWayRefoundableServiceAmountExtra  &&
						<>
							<Grid item xs={9} md={4} sx={{p: theme.spacing(1)}}>
								<Typography variant={"h6"}>
									{t("raCheckOutIn.summary.extraDetail.label.oneWayRefoundServiceAmount")}
								</Typography>
							</Grid>

							<Grid item xs={3} md={2} sx={{display: "flex", alignItems: "center", justifyContent: "flex-end", pr: theme.spacing(1)}}>
								<TextField
									value={`(${extraCostInputValues?.refundableAmountTaxIncluded ?? '0'})`}
									onChange={(e: React.SyntheticEvent) => handleOnChangeExtraCost(e, 'oneWayRefoundServiceAmount')}
									name='oneWayRefoundServiceAmount'
									fullWidth
									InputProps={{
										inputProps: {
											style: { textAlign: "right" }
										},
										endAdornment: <InputAdornment position="end">{'€'}</InputAdornment>
									}}
									disabled={disableOneWayRefoundServiceAmountExtra}
								/>
							</Grid>
						</>
					}
					{
						['in', 'subIn'].includes(checkOutInMode) && viewOneWayServiceAmountExtra &&
						<>
							<Grid item xs={9} md={4} sx={{ p: theme.spacing(1) }}>
								<Typography variant="h6">
									{t("raCheckOutIn.summary.extraDetail.label.oneWayExtraServiceAmount")}
									{(() => {
									// estraiamo il valore come number
									const raw = extraCostInputValues?.oneWayServiceAmount ?? "0";
									const num = parseFloat(raw.replace(",", "."));
									if (num > 0) {
										// formattiamo con due decimali
										const formatted = num.toFixed(2).replace(".", ",");
										return ` (${t("raCheckOutIn.summary.extraDetail.label.oneWayAmountRecalculated")} ${formatted})`;
									}
									return null;
									})()}
								</Typography>
							</Grid>

							<Grid item xs={3} md={2} sx={{display: "flex", alignItems: "center", justifyContent: "flex-end", pr: theme.spacing(1)}}>
								<TextField
									value={OneWayCost}
									onChange={(e: React.SyntheticEvent) => handleOnChangeExtraCost(e, 'oneWayExtraServiceAmount')}
									name='oneWayServiceAmount'
									fullWidth
									InputProps={{
										inputProps: {
											style: { textAlign: "right" }
										},
										endAdornment: <InputAdornment position="end">{'€'}</InputAdornment>
									}}
									disabled={disableOneWayServiceAmountExtra}
								/>
							</Grid>
						</>
					}
				</>
			}
		</Grid>

		{/* Equipments */}
		<Accordion sx={{mt: theme.spacing(1)}}>
			<AccordionSummary expandIcon={<ExpandMore/>}>
				<Typography variant={"h6"}>
					{t("raCheckOutIn.summary.equipments.title")}
				</Typography>

				{
					['in', 'subIn'].includes(checkOutInMode) &&
					<Typography variant={"h6"} sx={{ml: "auto", mr: theme.spacing(2)}}>
						{formatCurrency(locale as LOCALES, missingDotationAmount)}
					</Typography>
				}
			</AccordionSummary>

			<AccordionDetails>
				<Grid container columnSpacing={2}>
					{
						newMissingEquips
							.map((elem: any, index: number) =>
								<Grid container item xs={12} md={6} columnSpacing={2} key={'miss-quip-' + index}>
									<Grid item xs={8} sx={{display: 'flex', alignItems: 'center'}}>
										<Typography variant={"body"}>
											{elem.data.description}
										</Typography>
									</Grid>
									<Grid item xs={2} sx={{display: "flex", justifyContent: "flex-start"}}>
										<IconButton color="primary"
													onClick={(e: React.SyntheticEvent) => handleViewSnapshot(e, elem.base64Init + elem.checkPhoto)}>
											<VisibilityRounded/>
										</IconButton>
									</Grid>
									{
										['in', 'subIn'].includes(checkOutInMode) &&
										<Grid item xs={2} sx={{display: "flex", justifyContent: "flex-start"}}>
											{formatCurrency(locale as LOCALES, elem.data.priceInCents)}
										</Grid>
									}
								</Grid>
							)
					}

					{
						newMissingEquips.length === 0 &&
						<Grid item xs={12} columnSpacing={2}>
							<Typography variant={"body"}>
								{t("common.emptyState")}
							</Typography>
						</Grid>
					}

					{
						!!checkEquipments?.comments &&
						(checkEquipments as any).comments.length > 0 &&
						<Grid item xs={12} sx={{mt: theme.spacing(2)}}>
							<Typography variant={"h6"}>
								{t("raCheckOutIn.summary.equipments.label.comments")}
							</Typography>
						</Grid>
					}

					{
						!!checkEquipments?.comments &&
						(checkEquipments as any).comments
							.map((elem: any, index: number) =>
								<Grid item xs={12} key={"comment-" + index}
									  sx={{
										  borderRadius: 2,
										  bgcolor: theme.palette.neutral.main,
										  p: theme.spacing(1),
										  ml: theme.spacing(2),
										  mb: theme.spacing(1)
									  }}>
									<Typography variant={"body"} component={"span"} sx={{mr: theme.spacing(2)}}>
										{dayjs(elem.createDatetime).format('DD/MM/YYYY HH:mm')}
									</Typography>

									<Typography variant={"body"} component={"span"}>
										{elem.note}
									</Typography>
								</Grid>
							)
					}
				</Grid>
			</AccordionDetails>
		</Accordion>

		{/*<DamageSummary />*/}
		<Accordion>
			<AccordionSummary expandIcon={<ExpandMore />}>
				<Typography variant={"h6"}>
					{t("raCheckOutIn.summary.damage.title")}
				</Typography>

				{/* Check-In */}
				{
					['in', 'subIn'].includes(checkOutInMode) &&
					<Typography variant={"h6"} sx={{ml: "auto", mr: theme.spacing(2)}}>
						{
							formatCurrency(locale as LOCALES, damagePrice?.totalPrice)
						}
					</Typography>
				}
			</AccordionSummary>

			<AccordionDetails>
				<Grid container columnSpacing={2} sx={{pb: theme.spacing(1), pl: theme.spacing(2)}}>
					{/* External Damages*/}
					<Grid item xs={12} sx={{my: theme.spacing(1)}}>
						<Typography variant={"bodyLarge"}>
							{t("raCheckOutIn.summary.damage.label.preExistingDamage")}
						</Typography>
					</Grid>

					{/* ext old damage */}
					{
						!!existingExtDamage && existingExtDamage.length > 0 &&
						<>
							<Grid item xs={12}
								  sx={{py: theme.spacing(1), bgcolor: theme.palette.neutral.light,
									  borderTopLeftRadius: 8, borderTopRightRadius: 8}}>
								<Typography variant={"body"} component={"span"}>
									{t("raCheckOutIn.summary.damage.label.damage")}
								</Typography>
							</Grid>


							<Grid container item
								  sx={{py: theme.spacing(1), borderLeft: 1, borderRight:1, borderBottom: 1, borderBottomLeftRadius: 8, borderBottomRightRadius: 8,
									  borderColor: theme.palette.neutral.light}}>

									{
										existingExtDamage
											.map((damage: IDamage, index: number) =>  // damage: IDamage
												<Grid container item key={damage.id}>
													<Grid item xs={6} xl={3} sx={{display: "flex", alignItems: "center" }}>
														{damage.part?.descr} {damage.position?.descr !== 'NON DEFINITO' ? damage.position.descr : ''}
													</Grid>
													<Grid item xs={2} xl={1}  sx={{display: "flex",justifyContent: "center", alignItems: "center", py: theme.spacing(0.5)}}>
														<Chip label={damage.severity}
															  sx={{bgcolor: theme.palette.primary.main, color: theme.palette.neutral.light}} />
													</Grid>
													<Grid item xs={2} xl={1}
														  sx={{display: "flex", alignItems: "center", justifyContent: "flex-end"}}>
														{/* preview foto */}
														{
															!isMobile &&
															!!damage?.snapshotIdList &&
															damage.snapshotIdList
																.map((snapshot: any) =>
																	<IconButton key={damage.id} color="primary"
																				sx={{mx: theme.spacing(0.5)}}
																				onClick={(e: React.SyntheticEvent) => handleViewSnapshot(e, snapshot)}>
																		<VisibilityRounded />
																	</IconButton>
																)
														}

														{  isMobile &&
															<Box>
															<Button sx={{mx: theme.spacing(1)}}
																aria-controls={`menu-actions-${index}`}
																aria-haspopup="true"
																size={"icon"}
																color={'primary'}
																aria-expanded={!!anchorEl && selectedDamage === damage}
																onClick={(event) => handleOpenMenu(event, damage)}
																>
																<MoreVertRounded />
															</Button>
															<Menu
																id={`menu-actions-${index}`}
																anchorEl={anchorEl}
																open={!!anchorEl && selectedDamage === damage}
																onClose={handleClose}
															>

															{( !!damage?.snapshotIdList && damage.snapshotIdList.length > 0 ) &&
																damage.snapshotIdList
																.map((snapshot: number) => (
																	<MenuItem
																			key={damage.id + '-' + snapshot}
																			onClick={(e: React.SyntheticEvent) => {
																			handleClose();
																			handleViewSnapshot(e, snapshot);
																		}}
																	>
																		<IconButton key={snapshot} color="primary" sx={{mx: theme.spacing(0.5)}}>
																			<VisibilityRounded />
																		</IconButton>
																	</MenuItem>
																))}

															</Menu>
															</Box>
														}
													</Grid>

													<Grid item xs={2} xl={1}
														 sx={{display: "flex", alignItems: "center", justifyContent: "flex-end",
														 pr: theme.spacing(2), py: theme.spacing(0.5)}}>
														<Typography variant={"body"} sx={{pl: theme.spacing(1)}} />
													</Grid>
												</Grid>
											)
									}
							</Grid>
						</>
					}

					{
						!!existingExtDamage && existingExtDamage.length === 0 &&
						<Grid item xs={12} sx={{display: "flex", alignItems: "center" }}>
							{t("common.emptyState")}
						</Grid>
					}

					{/*	eventsExternal */}
					<Grid item xs={12} sx={{mb: theme.spacing(1), mt: theme.spacing(2)}}>
						<Typography variant={"bodyLarge"} component={"span"}>
							{t("raCheckOutIn.summary.damage.label.newDamage")}
						</Typography>
					</Grid>

					{
						eventsExternal
							.map((event: any, index: number) =>
								<Grid container item columnSpacing={2} key={event.id}>
									<Grid item xs={10}
										  sx={{py: theme.spacing(1), bgcolor: theme.palette.neutral.light,
											  borderTopLeftRadius: 8, pr: theme.spacing(1)}}>
										<Typography variant={"body"} component={"span"}>
											{getEventLabel(event.eventName) + ' '}
											<strong>
												{event.id}
											</strong>
										</Typography>

										{
											event.counterpart &&
											<Chip label={
												<Typography variant={"h6"} sx={{color: theme.palette.neutral.light}}>
													{t("raCheckOutIn.summary.damage.label.counterpart")}
												</Typography>
											}
												  sx={{mx: theme.spacing(1), bgcolor: theme.palette.primary.main}}
											/>
										}
									</Grid>
									{/* Event Total */}
									<Grid item xs={2} sx={{py: theme.spacing(1), bgcolor: theme.palette.neutral.light, borderTopRightRadius: 8,
										display: "flex", alignItems: "center", justifyContent: "flex-end", pr: theme.spacing(2)}}>
										{
											['in', 'subIn'].includes(checkOutInMode) &&
											<Typography variant={"h6"}>
												{
													// @ts-ignore
													formatCurrency(locale as LOCALES, totEvents?.[event.id])
												}
											</Typography>
										}
									</Grid>

									<Grid container item
										  sx={{py: theme.spacing(1),
											  borderLeft: 1, borderRight:1, borderBottom: 1,
											  borderBottomLeftRadius: 	['in', 'subIn'].includes(checkOutInMode) ? 0 : 8,
											  borderBottomRightRadius: 	['in', 'subIn'].includes(checkOutInMode) ? 0 : 8,
											  borderColor: theme.palette.neutral.light}}>
										{
											event.damages
												.map((damage: IDamage) =>
													<>
														<Grid item xs={6} xl={3} sx={{display: "flex", alignItems: "center" }}>
															<Typography variant={"body"}>
																{t2(`masterDataDamage.partName.${damage?.part?.code}`) + (damage?.position?.code != "6" ?  " " + t2(`masterDataDamage.positionName.${damage?.position?.code}`) : "")}
															</Typography>
														</Grid>

														<Grid item xs={2} xl={1} sx={{display: "flex", justifyContent: "center", alignItems: "center", py: theme.spacing(0.5)}}>
															<Chip label={operationTranslation(`damageList.severityValue.${damage.severity.toLowerCase()}` as any)}
																  sx={{bgcolor: theme.palette.primary.main, color: theme.palette.neutral.light}} />
														</Grid>
														<Grid item xs={2} xl={1} sx={{display: "flex", alignItems: "center", py: theme.spacing(0.5), justifyContent: "flex-end"}}>
															{/* preview foto */}
															{ !isMobile &&
																!!damage?.snapshotIdList &&
																damage.snapshotIdList
																	.map((snapshot: any) =>
																		<IconButton key={damage.id} color="primary"
																					sx={{mx: theme.spacing(0.5)}}
																					onClick={(e: React.SyntheticEvent) => handleViewSnapshot(e, snapshot)}>
																			<VisibilityRounded />
																		</IconButton>
																	)
															}

															{  isMobile &&
																<Box>
																	<Button sx={{mx: theme.spacing(1)}}
																			aria-controls={`menu-actions-${index}`}
																			aria-haspopup="true"
																			size={"icon"}
																			color={'primary'}
																			aria-expanded={!!anchorEl && selectedDamage === damage}
																			onClick={(event) => handleOpenMenu(event, damage)}
																	>
																		<MoreVertRounded />
																	</Button>
																	<Menu
																		id={`menu-actions-${index}`}
																		anchorEl={anchorEl}
																		open={!!anchorEl && selectedDamage === damage}
																		onClose={handleClose}
																	>

																		{( !!damage?.snapshotIdList && damage.snapshotIdList.length > 0 ) &&
																			damage.snapshotIdList
																				.map((snapshot: number) => (
																					<MenuItem
																						key={snapshot}
																						onClick={(e: React.SyntheticEvent) => {
																							handleClose();
																							handleViewSnapshot(e, snapshot);
																						}}
																					>
																						<IconButton key={snapshot} color="primary" sx={{mx: theme.spacing(0.5)}}>
																							<VisibilityRounded />
																						</IconButton>
																					</MenuItem>
																				))}

																	</Menu>
																</Box>
															}
														</Grid>
														<Grid item xs={2} xl={1}
															  sx={{display: "flex", alignItems: "center", justifyContent: "flex-end",
																  pr: theme.spacing(2), py: theme.spacing(0.5)}}>
															<Typography variant={"body"} sx={{pl: theme.spacing(1)}}>
																{/*{damage.price}*/}
																{formatCurrency(locale as LOCALES, damagePrice?.damagePrices.find((e: { damageId: number | null; }) => e.damageId === damage.id)?.price)}
															</Typography>
														</Grid>
													</>
												)
										}
									</Grid>

									{/* Damage Cost */}
									{
										['in', 'subIn'].includes(checkOutInMode) &&
										<>
											<Grid container item sx={{bgcolor: theme.palette.neutral.main}}>
												<Grid item xs={10} sx={{display: "flex", justifyContent: "start-end", alignItems: "center", p: theme.spacing(1)}}>
													<Typography variant={"h6"}>
														{t("raCheckOutIn.summary.damage.label.damageExcess")}
													</Typography>
												</Grid>
												<Grid item xs={2} sx={{display: "flex", justifyContent: "flex-end", alignItems: "center", p: theme.spacing(1)}}>
													<Typography variant={"h6"} component={"span"} sx={{px: theme.spacing(1)}}>
														{formatCurrency(locale as LOCALES, damageExcess)}
													</Typography>
												</Grid>
											</Grid>

											<Grid container item sx={{bgcolor: theme.palette.primary.main, mb: theme.spacing(2), borderBottomLeftRadius: 8, borderBottomRightRadius: 8}}>
												<Grid item xs={10} sx={{display: "flex", justifyContent: "start-end", alignItems: "center", p: theme.spacing(1), }}>
													<Typography variant={"h6"} sx={{color: theme.palette.neutral.light}}>
														{t("raCheckOutIn.summary.damage.label.totalAmount")}
													</Typography>
												</Grid>
												<Grid item xs={2} sx={{px: theme.spacing(1), display: "flex", justifyContent: "flex-end", alignItems: "center", p: theme.spacing(1) }}>
													<Typography variant={"h6"} component={"span"} sx={{color: theme.palette.neutral.light}}>
														{
															// @ts-ignore
															formatCurrency(locale as LOCALES, eventsDmgAmount?.[event.id])
														}
													</Typography>
												</Grid>
											</Grid>
										</>
									}
								</Grid>
							)
					}

					{
						!!eventsExternal && eventsExternal.length === 0 &&
						<Grid item xs={12} columnSpacing={2} sx={{mb: theme.spacing(1)}}>
							<Typography variant={"body"}>
								{t("common.emptyState")}
							</Typography>
						</Grid>
					}

					{/*/!* Internal damage *!/*/}
					{/*<Grid item xs={6} sx={{mb: theme.spacing(1), mt: theme.spacing(3)}}>*/}
					{/*	<Typography variant={"bodyLarge"}>*/}
					{/*		{t("Danni Interni")}*/}
					{/*	</Typography>*/}
					{/*</Grid>*/}

					{/*/!* int old damage *!/*/}
					{/*<Grid container item columnSpacing={2} sx={{mb: theme.spacing(2)}}>*/}
					{/*	<Grid item xs={12}*/}
					{/*		  sx={{py: theme.spacing(1), bgcolor: theme.palette.neutral.light,*/}
					{/*			  borderTopLeftRadius: 8, borderTopRightRadius: 8}}>*/}
					{/*		<Typography variant={"body"} component={"span"}>*/}
					{/*			{t("Danni Esistenti")}*/}
					{/*		</Typography>*/}
					{/*	</Grid>*/}

					{/*	<Grid container item*/}
					{/*		  sx={{py: theme.spacing(1), borderLeft: 1, borderRight:1, borderBottom: 1,*/}
					{/*			  borderBottomLeftRadius: 8, borderBottomRightRadius: 8,*/}
					{/*			  borderColor: theme.palette.neutral.light}}>*/}
					{/*		{*/}
					{/*			existingIntDamage*/}
					{/*				.map((damage: IDamage) =>*/}
					{/*					<>*/}
					{/*						<Grid item xs={3} sx={{display: "flex", alignItems: "center" }}>*/}
					{/*							{damage.part.descr}*/}
					{/*						</Grid>*/}
					{/*						<Grid item xs={2} sx={{display: "flex", alignItems: "center", py: theme.spacing(0.5)}}>*/}
					{/*							<Chip label={damage.severity}*/}
					{/*								  sx={{bgcolor: theme.palette.primary.main, color: theme.palette.neutral.light}} />*/}
					{/*						</Grid>*/}
					{/*						<Grid item xs={1}*/}
					{/*							  sx={{display: "flex", alignItems: "center", justifyContent: "flex-start",*/}
					{/*								  pr: theme.spacing(2), py: theme.spacing(0.5)}}>*/}
					{/*							/!* preview foto *!/*/}
					{/*							{*/}
					{/*								!!damage?.snapshotIdList &&*/}
					{/*								damage.snapshotIdList*/}
					{/*									.map((file: any) =>*/}
					{/*										<IconButton key={damage.id} color="secondary"*/}
					{/*													sx={{mx: theme.spacing(0.5)}}*/}
					{/*													onClick={(e: React.SyntheticEvent) => handleViewSnapshot(e, file.documentFile)}*/}
					{/*										>*/}
					{/*											<VisibilityRounded />*/}
					{/*										</IconButton>*/}
					{/*									)*/}
					{/*							}*/}
					{/*						</Grid>*/}
					{/*					</>*/}
					{/*				)*/}
					{/*		}*/}

					{/*		{*/}
					{/*			!!existingIntDamage && existingIntDamage.length === 0 &&*/}
					{/*			<Grid item xs={12} sx={{display: "flex", alignItems: "center" }}>*/}
					{/*				{t("Nessun Danno")}*/}
					{/*			</Grid>*/}
					{/*		}*/}

					{/*	</Grid>*/}
					{/*</Grid>*/}

					{/*/!*	eventsInternal *!/*/}
					{/*<Grid item xs={12} sx={{mb: theme.spacing(1)}}>*/}
					{/*	<Typography variant={"bodyLarge"} component={"span"}>*/}
					{/*		{t("Eventi")}*/}
					{/*	</Typography>*/}
					{/*</Grid>*/}

					{/*{*/}
					{/*	// eventsInternal*/}
					{/*	eventsInternal*/}
					{/*		.map((event: IEvent, index: number) =>*/}
					{/*			<Grid container item columnSpacing={2} key={event.id} sx={{mb: theme.spacing(2)}}>*/}
					{/*				<Grid item xs={11}*/}
					{/*					  sx={{py: theme.spacing(1), bgcolor: theme.palette.neutral.light,*/}
					{/*						  borderTopLeftRadius: 8,*/}
					{/*						  borderTopRightRadius: 8}}>*/}
					{/*					<Typography variant={"body"}>*/}
					{/*						{event.eventName?.descr + ' '}*/}
					{/*						<strong>*/}
					{/*							{event.id}*/}
					{/*						</strong>*/}
					{/*					</Typography>*/}
					{/*				</Grid>*/}

					{/*				<Grid item xs={1} sx={{py: theme.spacing(1), bgcolor: theme.palette.neutral.light, borderTopRightRadius: 8,*/}
					{/*					display: "flex", alignItems: "center", justifyContent: "flex-end", pr: theme.spacing(2)}}>*/}
					{/*					<Typography variant={"h6"}>*/}
					{/*						{['in', 'subIn'].includes(checkOutInMode) && summary?.eventInt[index]}*/}
					{/*					</Typography>*/}
					{/*				</Grid>*/}


					{/*				<Grid container item*/}
					{/*					  sx={{py: theme.spacing(1), borderLeft: 1, borderRight:1, borderBottom: 1, borderBottomLeftRadius: 8, borderBottomRightRadius: 8,*/}
					{/*						  borderColor: theme.palette.neutral.light}}>*/}
					{/*					{*/}
					{/*						event.damages*/}
					{/*							.map((damage: IDamage) =>*/}
					{/*								<>*/}
					{/*									<Grid item xs={3} sx={{display: "flex", alignItems: "center" }}>*/}
					{/*										{damage.part.descr}*/}
					{/*									</Grid>*/}
					{/*									<Grid item xs={1} sx={{display: "flex", alignItems: "center", py: theme.spacing(0.5)}}>*/}
					{/*										<Chip label={damage.severity}*/}
					{/*											  sx={{bgcolor: theme.palette.primary.main, color: theme.palette.neutral.light}} />*/}
					{/*									</Grid>*/}
					{/*									<Grid item xs={1} sx={{display: "flex", alignItems: "center", py: theme.spacing(0.5), justifyContent: "flex-end"}}>*/}
					{/*										/!* preview foto *!/*/}
					{/*										{*/}
					{/*											!!damage?.snapshotIdList &&*/}
					{/*											damage.snapshotIdList*/}
					{/*												.map((file: any) =>*/}
					{/*													<IconButton key={damage.id} color="secondary"*/}
					{/*																sx={{mx: theme.spacing(0.5)}}*/}
					{/*																onClick={(e: React.SyntheticEvent) => handleViewSnapshot(e, file.documentFile)}>*/}
					{/*														<VisibilityRounded />*/}
					{/*													</IconButton>*/}
					{/*												)*/}
					{/*										}*/}
					{/*									</Grid>*/}
					{/*									<Grid item xs={1}*/}
					{/*										  sx={{display: "flex", alignItems: "center", justifyContent: "flex-end",*/}
					{/*											  pr: theme.spacing(2), py: theme.spacing(0.5)}}>*/}
					{/*										/!*{damage.price}*!/*/}
					{/*									</Grid>*/}
					{/*								</>*/}
					{/*							)*/}
					{/*					}*/}
					{/*				</Grid>*/}
					{/*			</Grid>*/}
					{/*		)*/}
					{/*}*/}

					{/*{*/}
					{/*	!!eventsInternal && eventsInternal.length === 0 &&*/}
					{/*	<Grid item xs={12} columnSpacing={2}>*/}
					{/*		<Typography variant={"body"}>*/}
					{/*			{t("Danni assenti")}*/}
					{/*		</Typography>*/}
					{/*	</Grid>*/}
					{/*}*/}

					{/*/!* Damage Cost *!/*/}
					{/*{*/}
					{/*	['in', 'subIn'].includes(checkOutInMode) &&*/}
					{/*	<>*/}
					{/*	<Grid container item sx={{mt: theme.spacing(2), p: theme.spacing(1), bgcolor: theme.palette.neutral.main, borderTopLeftRadius: 8, borderTopRightRadius: 8}}>*/}
					{/*			<Grid item xs={10} sx={{display: "flex", justifyContent: "start-end", alignItems: "center"}}>*/}
					{/*				<Typography variant={"h6"}>*/}
					{/*					{t("Franchiggia")}*/}
					{/*				</Typography>*/}
					{/*			</Grid>*/}
					{/*			<Grid item xs={2} sx={{display: "flex", justifyContent: "flex-end", alignItems: "center"}}>*/}
					{/*				<Typography variant={"h6"} component={"span"} sx={{px: theme.spacing(1)}}>*/}
					{/*					{formatCurrency(locale as LOCALES, damageExcess)}*/}
					{/*				</Typography>*/}
					{/*			</Grid>*/}
					{/*		</Grid>*/}

					{/*		<Grid container item sx={{p: theme.spacing(1), bgcolor: theme.palette.primary.main, borderBottomLeftRadius: 8, borderBottomRightRadius: 8}}>*/}
					{/*			<Grid item xs={10} sx={{display: "flex", justifyContent: "start-end", alignItems: "center"}}>*/}
					{/*				<Typography variant={"h6"} sx={{color: theme.palette.neutral.light}}>*/}
					{/*					{t("Costo totale")}*/}
					{/*				</Typography>*/}
					{/*			</Grid>*/}
					{/*			<Grid item xs={2} sx={{px: theme.spacing(1), display: "flex", justifyContent: "flex-end", alignItems: "center"}}>*/}
					{/*				<Typography variant={"h6"} component={"span"} sx={{color: theme.palette.neutral.light}}>*/}
					{/*					{*/}
					{/*						// formatCurrency(*/}
					{/*						// 	locale as LOCALES,*/}
					{/*						// 	damagesTotalCost*/}
					{/*						// )*/}
					{/*					}*/}
					{/*				</Typography>*/}
					{/*			</Grid>*/}
					{/*		</Grid>*/}
					{/*	</>*/}
					{/*}*/}
				</Grid>
			</AccordionDetails>
		</Accordion>

		{/* Amount */}
		{
			['in', 'subIn'].includes(checkOutInMode) &&
			<Grid container columnSpacing={2} sx={{my: theme.spacing(2), pl: theme.spacing(2)}}>
				<Grid container item xs={12} sx={{py: theme.spacing(1), my: theme.spacing(1),
					bgcolor: theme.palette.neutral.main,
					borderRadius: 2}}>
					<Grid item xs={10} sx={{display: "flex", justifyContent: "start-end", alignItems: "center"}}>
						<Typography variant={"h6"}>
							{t("raCheckOutIn.summary.amount.label.coveredCostDamage")}
						</Typography>
					</Grid>
					<Grid item xs={2} sx={{display: "flex", justifyContent: "flex-end", alignItems: "center"}}>
						<Typography variant={"h6"} component={"span"} sx={{px: theme.spacing(1)}}>
							{formatCurrency(locale as LOCALES, coveredCost)}
						</Typography>
					</Grid>
				</Grid>
				<Grid container item xs={12} sx={{py: theme.spacing(1), my: theme.spacing(1),
					bgcolor: theme.palette.neutral.main,
					borderRadius: 2}}>
					<Grid item xs={10} sx={{display: "flex", justifyContent: "start-end", alignItems: "center"}}>
						<Typography variant={"h6"}  >
							{t("raCheckOutIn.summary.amount.label.totalAmountToPay")}
						</Typography>
					</Grid>
					<Grid item xs={2} sx={{px: theme.spacing(1),
						display: "flex", justifyContent: "flex-end", alignItems: "center"}}>
						<Typography variant={"h6"} component={"span"} sx={{px: theme.spacing(1)}}>
							{
								formatCurrency(locale as LOCALES, totDmgsEquips)
							}
						</Typography>
					</Grid>
				</Grid>
				<Grid container item xs={12} sx={{py: theme.spacing(1), my: theme.spacing(1),
					bgcolor: theme.palette.primary.main,
					borderRadius: 2}}>
					<Grid item xs={10} sx={{display: "flex", justifyContent: "start-end", alignItems: "center"}}>
						<Typography variant={"h6"}  sx={{color: theme.palette.neutral.light}}>
							{t("raCheckOutIn.summary.amount.label.total")}
						</Typography>
					</Grid>
					<Grid item xs={2} sx={{px: theme.spacing(1),
						display: "flex", justifyContent: "flex-end", alignItems: "center"}}>
						<Typography variant={"h6"} component={"span"} sx={{px: theme.spacing(1), color: theme.palette.neutral.light}}>
							{
								formatCurrency(locale as LOCALES, summary?.total)
							}
						</Typography>
					</Grid>
				</Grid>
			</Grid>
		}

		<CheckStepper />

		<Box sx={{display: "flex", justifyContent: "center", mt: theme.spacing(2)}}>
			<Button color={"accent"} onClick={handleContinue}
				disabled={(['in', 'subIn'].includes(checkOutInMode) && !(!!summary && !isNaN(summary.total))) || !!raCheckInExtra?.flagError}
			>
				{
					!!raFetchedData.oprData?.callbackButtonLabel ?
						raFetchedData.oprData?.callbackButtonLabel:
						t('raCheckOutIn.summary.button.continue')
				}
			</Button>
		</Box>

		<Preview
			open={openPreviewAttach}
			attach={filePreview}
			onClose={handleClosePreview}
		/>

		<CustomLoader  loading={damagePriceMutation.isLoading || confirmFormMutation.isLoading ||
			fetchCheckOutInGetSnapshotBlogByIdMutation.isLoading ||	fetchCheckInExtraMutation.isLoading} />
	</Paper>);
}

export default Summary;
